import BottomDrawerSelectOptions, {
	BottomDrawerSelectOptionsProps,
} from 'src/components/Inputs/BottomDrawerSelect/BottomDrawerSelectOptions';
import { ReactNode, useCallback, useState } from 'react';
import useCloseBottomDrawer from 'src/components/Drawers/BaseDrawers/BottomDrawer/hooks/useCloseBottomDrawer';
import { BottomDrawerSelectOption } from 'src/components/Inputs/BottomDrawerSelect/types';
import DropdownUi from 'src/components/primitives/DropdownUi/DropdownUi';

interface Props<T> extends Pick<
	BottomDrawerSelectOptionsProps<T>,
	'selectedValue' | 'onSelect' | 'noSelectedCheckmark'
> {
	label: string;
	/**
	 * The value displayed in the trigger. Typically the label of the selected option.
	 */
	triggerValue: ReactNode;
	disabled?: boolean;
	options: BottomDrawerSelectOption<T>[];
}

/**
 * Renders an input resembling a `select`, but opens a bottom drawer instead
 * of a dropdown.
 *
 * Only intended for use on mobile.
 */
export default function BottomDrawerSelect<T>({
	label,
	triggerValue,
	disabled,
	options,
	onSelect,
	selectedValue,
	noSelectedCheckmark,
}: Props<T>) {
	const [isOpen, setIsOpen] = useState(false);

	const { closeDrawer, isDrawerClosing } = useCloseBottomDrawer({
		onClose: () => setIsOpen(false),
	});

	const handleSelect = useCallback(
		(value: T) => {
			onSelect(value);
			closeDrawer();
		},
		[onSelect, closeDrawer]
	);

	return (
		<>
			<DropdownUi.Trigger
				label={label}
				disabled={disabled}
				onClick={() => !disabled && setIsOpen(!isOpen)}
			>
				{triggerValue}
				<DropdownUi.Chevron />
			</DropdownUi.Trigger>

			{isOpen && (
				<BottomDrawerSelectOptions
					title={label}
					options={options}
					onSelect={handleSelect}
					selectedValue={selectedValue}
					closeDrawer={closeDrawer}
					isDrawerClosing={isDrawerClosing}
					noSelectedCheckmark={noSelectedCheckmark}
				/>
			)}
		</>
	);
}
